import React, { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../../../store/auth/authSlice'
import { TemplateInputParameters } from '../../../../../../models/AiFeature'
import { YesNoCancelModal } from '../../../../../../components/Modal/YesNoCancelModal'
import { PresetTemplateConfigForm } from '../PresetTemplateConfigForm'
import { Button } from '../../../../../../components/Button/Button'
import { AiFeaturePreset } from '../../../../../../models/AiFeaturePreset'
import { useGlobalTemplatePresetInputParameters } from '../../hooks'
import { PresetConfigLoader } from '../../Common/PresetConfigLoader'
import { ScopedBlock } from '../../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../../components/ScopedBlock/models'
import { useUserTemplateConfigForm } from './hooks'
import {
  InputParameterHierarchyType,
  MeetingTemplateInputParametersHierarchyConfigComponentProps,
} from '../../../../../../components/InputParameterHierarchy/model'
import { useMeetingTemplateEffectiveInputParameters, useInputParametersUpdateScope } from '../../../../../../components/InputParameterHierarchy/hooks'
import { useGetPresetByIdQuery } from '../../../../../../store/ai/ai'

export const CurrentUserPresetTemplateConfigForm: React.FC<MeetingTemplateInputParametersHierarchyConfigComponentProps> = ({
  typeId,
  meetingTemplateId,
  showCustomizationSetting,
  onHide,
  ipHierarchyEnabled,
}) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: presetData } = useGetPresetByIdQuery({ accountId, presetId: typeId })

  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const { presetInputParameters, presetDefaultInputParameters } = useGlobalTemplatePresetInputParameters(presetData?.data)
  const { inputParameters, parentInputParameters, updateLoading, updateInputParameters, isCustomized } = useMeetingTemplateEffectiveInputParameters(
    InputParameterHierarchyType.AI_FEATURE_PRESET,
    typeId,
    presetInputParameters,
    currentUserId,
    meetingTemplateId,
  )

  if (!presetData || !presetData.data || !inputParameters || !presetDefaultInputParameters || !parentInputParameters) {
    return <PresetConfigLoader />
  }

  const { example_1 = '', example_2 = '', template } = inputParameters
  const { example_1: defaultExample_1 = '', example_2: defaultExample_2 = '', template: defaultTemplate } = presetDefaultInputParameters
  const { example_1: parentExample_1 = '', example_2: parentExample_2 = '', template: parentTemplate } = parentInputParameters

  return (
    <CurrentUserTemplateConfigFormBody
      preset={presetData.data}
      inputParameters={{ example_1, example_2, template }}
      defaultInputParameters={{ example_1: defaultExample_1, example_2: defaultExample_2, template: defaultTemplate }}
      parentInputParameters={{ example_1: parentExample_1, example_2: parentExample_2, template: parentTemplate }}
      isCustomized={isCustomized}
      showCustomizationSetting={showCustomizationSetting}
      onSave={updateInputParameters}
      onHide={onHide}
      updateLoading={updateLoading}
      ipHierarchyEnabled={ipHierarchyEnabled}
    />
  )
}

interface CurrentUserTemplateConfigFormBodyProps {
  preset: AiFeaturePreset
  defaultInputParameters: TemplateInputParameters
  parentInputParameters: TemplateInputParameters
  inputParameters: TemplateInputParameters
  isCustomized: boolean
  showCustomizationSetting: boolean
  onSave: (inputParameters: TemplateInputParameters, isCustomized: boolean) => Promise<void>
  onHide: () => void
  updateLoading: boolean
  ipHierarchyEnabled: boolean
}

const CurrentUserTemplateConfigFormBody: React.FC<CurrentUserTemplateConfigFormBodyProps> = ({
  preset,
  defaultInputParameters,
  parentInputParameters,
  inputParameters,
  isCustomized,
  showCustomizationSetting,
  onSave,
  onHide,
  updateLoading,
  ipHierarchyEnabled,
}) => {
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { hasIpHierarchyUpdateScope } = useInputParametersUpdateScope(currentUserId)
  const disabled = !ipHierarchyEnabled || !hasIpHierarchyUpdateScope

  const {
    editedInputParameters,
    isChanged,
    customized,
    showConfirmation,
    shouldReset,
    isGenerating,
    showGenerate,
    setEditedInputParameters,
    setShouldReset,
    setShowConfirmation,
    setShowGenerate,
    setGenerating,
    handleChangeCustomized,
    handleChangeExample,
    handleChangeTemplate,
    handleClickBack,
    handleClickCancel,
    handleClickSave,
    checkForChange,
  } = useUserTemplateConfigForm(inputParameters, parentInputParameters, isCustomized, onSave, onHide, disabled)

  useEffect(() => {
    checkForChange()
    setShouldReset(false)
  }, [checkForChange])

  return (
    <>
      <YesNoCancelModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        message={'Do you want to save your changes?'}
        onYes={() => {
          onSave(editedInputParameters, customized)
          onHide()
        }}
        onNo={() => {
          setEditedInputParameters(inputParameters)
          onHide()
        }}
      />
      <PresetTemplateConfigForm
        preset={preset}
        defaultInputParameters={defaultInputParameters}
        inputParameters={inputParameters}
        parentInputParameters={parentInputParameters}
        editedInputParameters={editedInputParameters}
        showCustomizationSetting={showCustomizationSetting}
        customized={customized}
        shouldReset={shouldReset}
        shouldShowGenerate={showGenerate}
        showGenerate={() => setShowGenerate(true)}
        onChangeTemplate={handleChangeTemplate}
        onChangeExample={handleChangeExample}
        onChangeCustomized={handleChangeCustomized}
        onGenerate={setGenerating}
        disabled={disabled}
      />

      <div className='d-flex justify-content-between w-100 mt-3'>
        <Button variant='borderless' onClick={handleClickBack}>
          {showGenerate ? 'Back' : 'Close'}
        </Button>
        {hasIpHierarchyUpdateScope && (
          <div className='d-flex gap-2'>
            <>
              {ipHierarchyEnabled && (
                <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading || isGenerating}>
                  Cancel
                </Button>
              )}
              <ScopedBlock
                missingScopeAction={MissingScopeAction.UPGRADE}
                quota={{
                  quota: 'ai.ipHierarchy.value',
                }}
              >
                <Button variant='primary' onClick={handleClickSave} disabled={ipHierarchyEnabled && !isChanged} loading={updateLoading || isGenerating}>
                  {ipHierarchyEnabled ? 'Save' : 'Get feature'}
                </Button>
              </ScopedBlock>
            </>
          </div>
        )}
      </div>
    </>
  )
}
